import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { fetchPostById, fetchLatestPosts } from "../services/firestore";
import Comments from "../components/Comments";
import ArticleCard from "../components/ArticleCard";
import Sidebar from "../components/Sidebar";
import "./PostPage.css";
import { Helmet } from "react-helmet-async";

function timeAgo(timestamp) {
  if (!timestamp) return ""; 
  const date = timestamp?.toDate ? timestamp.toDate() : new Date(timestamp);
  return date.toLocaleDateString("en-GB", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

export default function PostPage() {
  const { id } = useParams();
  const [post, setPost] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    window.scrollTo(0, 0);
    setLoading(true);
    Promise.all([fetchPostById(id), fetchLatestPosts(4)]).then(
      ([p, latest]) => {
        setPost(p);
        setRelated(latest.filter((l) => l.id !== id).slice(0, 3));
        setLoading(false);
      }
    );
  }, [id]);

  const handleShare = (type) => {
    if (!post) return;
    const postUrl = `${window.location.origin}/post/${post.id}`;
    const text = `${post.title}${post.excerpt ? ` - ${post.excerpt}` : ''}`;
    let shareUrl = '';

    switch (type) {
      case 'facebook':
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(postUrl)}`;
        break;
      case 'twitter':
        shareUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(postUrl)}`;
        break;
      case 'whatsapp':
        shareUrl = `https://api.whatsapp.com/send?text=${encodeURIComponent(text + ' ' + postUrl)}`;
        break;
      default:
        return;
    }

    window.open(shareUrl, '_blank', 'noopener,noreferrer');
  };

  if (loading) {
    return (
      <div className="post-loading">
        <div className="loading-spinner" />
        <p>Loading article...</p>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="container post-not-found">
        <h2>Article not found</h2>
        <Link to="/" className="back-home">
          ← Back to Home
        </Link>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{post.title} | My Blog</title>
        <meta property="og:type" content="article" />
        <meta property="og:title" content={post.title} />
        <meta property="og:description" content={post.excerpt || "Read this article on My Blog."} />
        <meta property="og:image" content={post.imageUrl} />
        <meta property="og:image:secure_url" content={post.imageUrl} />
        <meta property="og:url" content={`https://nigeriapublishers.com.ng/post/${post.id}`} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={post.title} />
        <meta name="twitter:description" content={post.excerpt || "Read this article on My Blog."} />
        <meta name="twitter:image" content={post.imageUrl} />
      </Helmet>
      <div className="container post-layout">
        <main className="post-main">
          <article className="post-article">
            <div className="post-breadcrumb">
              <Link to="/">Home</Link>
              <span> / </span>
              <Link to={`/category/${post.category?.toLowerCase()}`}>
                {post.category}
              </Link>
            </div>

            <h1 className="post-title">{post.title}</h1>

            <div className="post-meta">
              {post.authorAvatar && (
                <img
                  src={post.authorAvatar}
                  alt={post.author}
                  className="post-author-avatar"
                />
              )}
              <div className="post-meta-info">
                <span className="post-author">{post.author}</span>
                <span className="post-date">{timeAgo(post.timestamp)}</span>
              </div>
              {post.views && (
                <span className="post-views">
                  👁 {post.views.toLocaleString()} views
                </span>
              )}
            </div>

            <div className="post-hero-img-wrap">
              <img
                src={post.imageUrl}
                alt={post.title}
                className="post-hero-img"
              />
            </div>

            {post.excerpt && (
              <p className="post-excerpt">{post.excerpt}</p>
            )}

            <div
              className="post-content"
              dangerouslySetInnerHTML={{ __html: post.content }}
            />

            <div className="post-tags">
              <Link
                to={`/category/${post.category?.toLowerCase()}`}
                className="post-tag"
              >
                {post.category}
              </Link>
            </div>

            <div className="post-share">
              <span className="post-share-label">Share:</span>
              <button className="share-btn share-fb" onClick={() => handleShare('facebook')}>Facebook</button>
              <button className="share-btn share-tw" onClick={() => handleShare('twitter')}>Twitter</button>
              <button className="share-btn share-wa" onClick={() => handleShare('whatsapp')}>WhatsApp</button>
            </div>
          </article>

          {/* Related */}
          {related.length > 0 && (
            <section className="post-related">
              <div className="section-title">
                <span className="section-title-bar" />
                <h2>Related Posts</h2>
              </div>
              <div className="related-grid">
                {related.map((p) => (
                  <ArticleCard key={p.id} post={p} size="medium" />
                ))}
              </div>
            </section>
          )}

          <Comments postId={id} />
        </main>

        <Sidebar />
      </div>
    </>
  );
}
